// Admin-Wünsche W2 (output/admin-wuensche-20261008/PLAN.md 2.2; Vorlauf V-1): Wörter zu einem Nutzungsbalken. Reine
// Funktionen über den Vertrag in administration/kontingent.ts; Prozent und Zustand entscheidet der Server, prozentAus und
// zustandAus beschreiben dieselbe Regel für Stellen ohne Serverwert und für die Tests. Geprüft in
// frontend/tests/nutzungsbalken.test.mjs.
// Spur kontingent, KO-23 (SPEC-KONTINGENT.md KT-10 bis KT-14, KT-20, KT-40, KT-49; 2.1, 3.4, 3.5): Töpfe der KI stehen nur in
// Prozent, mit Bezug und „Beginnt am {Tag} neu“; der Zustandssatz kennt die Art des Balkens. Kein Betrag mehr für Kontingent,
// Zusatzkontingent oder persönliche Grenze; „12 von 50 Recherchen“ bleibt für die Anzahl (anzahlSatz). Diese Datei ruft keinen
// Geldbaustein mehr auf (Betrag-Ratsche Ziel 0, SPEC-KONTINGENT.md 7.2).
import type { Anteil, Balken, BalkenZustand } from '../../administration/kontingent';
import { prozentAnzeige } from '../../administration/billing/prozent';
import { vomKontingentText } from '../../administration/billing/kontingentSaetze';
import { monatsBeginn, naechsterMonatsBeginn } from '../../administration/billing/kontingentMonat';

export const FAST_AB = 80;
export type BalkenFarbe = 'aktion' | 'warnung' | 'gefahr';
/** Art des Topfs für die Wörter (KT-13): Kontingent und Zusatzkontingent „aufgebraucht“, persönliche Grenze und
 *  Anzahlgrenze „Grenze erreicht“. */
export type TopfArt = 'KONTINGENT' | 'ZUSATZ' | 'GRENZE' | 'RECHERCHEN';
/** Was nach dem Topf weiterträgt (KT-14): das Zusatzkontingent oder der freigegebene Verbrauch über dem Kontingent; nach dem
 *  letzten Topf der Firma das zusätzliche Guthaben, solange es trägt (14.1 Punkt 3; Schlusslauf 5, H-1). */
export type Weiter = 'ZUSATZ' | 'UEBER' | 'GUTHABEN' | null;

// Beträge auf Mikro-Cent genau als ganze Zahlen vergleichen (1 Cent = 10 000 Mikro, wie das Kostenjournal), damit
// Gleitkommareste wie 0,57 × 100 = 56,999… weder einen Prozentpunkt verlieren noch 100 % vor der Grenze zeigen.
const mikro = (wert: number) => Math.round(wert * 10000);

/** floor(verwendet × 100 / grenze); 100 erst an der Grenze, darüber nur bei weichem Modus; Grenze 0 gilt als erreicht. */
export function prozentAus(verwendet: number, grenze: number): number {
  if (mikro(grenze) <= 0) return 100;
  return Math.floor(mikro(verwendet) * 100 / mikro(grenze));
}

export function zustandAus(verwendet: number, grenze: number): BalkenZustand {
  if (mikro(grenze) <= 0 || mikro(verwendet) >= mikro(grenze)) return 'ERREICHT';
  return prozentAus(verwendet, grenze) >= FAST_AB ? 'FAST' : 'OK';
}

/** „37 % verwendet“; zwischen 0 und 1 „unter 1 % verwendet“ (3.4). */
export const prozentText = (prozent: number, unterEinem = false) =>
  unterEinem && prozent < 1 ? 'unter 1 % verwendet' : `${prozent.toLocaleString('de-AT')} % verwendet`;

/** Rechts am Balken (3.4): bei einem Topf der KI ohne Verbrauch „noch nichts verwendet“, sonst wie prozentText. */
export function standRechts(balken: Pick<Balken, 'art' | 'prozent' | 'unterEinem'>): string {
  if (balken.art !== 'RECHERCHEN' && balken.prozent === 0 && !balken.unterEinem) return 'noch nichts verwendet';
  return prozentText(balken.prozent, !!balken.unterEinem);
}

/** Wert für aria-valuenow und die Breite der Füllung: 0 bis 100, auch wenn ein weiches Budget überschritten ist. Ein Stand
 *  unter 1 % zeigt die kleinste sichtbare Füllung (3.4). */
export const ariaWert = (prozent: number) => Math.min(100, Math.max(0, prozent));
export const fuellung = (balken: Pick<Balken, 'prozent' | 'unterEinem'>) => balken.unterEinem && balken.prozent < 1 ? 1 : ariaWert(balken.prozent);

/** Farbe nach dem eigenen Stand; ein Topf, nach dem es weitergeht (KT-14), ist auch voll keine Sperre. */
export function balkenFarbe(balken: Pick<Balken, 'zustand' | 'modus'>, weiter: Weiter = null): BalkenFarbe {
  if (balken.zustand === 'ERREICHT') return balken.modus === 'HARD' && !weiter ? 'gefahr' : 'warnung';
  return balken.zustand === 'FAST' ? 'warnung' : 'aktion';
}

/**
 * Zustandssatz am Balken (KT-13, KT-14). Kontingent und Zusatzkontingent: „Fast aufgebraucht.“, „Aufgebraucht. …“;
 * persönliche Grenze (und Anzahlgrenze): „Grenze fast erreicht.“, „Grenze erreicht. …“. Trägt ein weiterer Topf weiter, heißt
 * es „Fast ganz verwendet. …“ bzw. „Ganz verwendet. …“, nie „aufgebraucht“ (3.5); ueberGroesse ist die Obergrenze des
 * Verbrauchs über dem Kontingent in Prozent („bis 10 % darüber“).
 */
export function zustandsSatz(balken: Pick<Balken, 'zustand' | 'modus'>, art: TopfArt = 'GRENZE', weiter: Weiter = null,
  ueberGroesse: number | null = null): string | null {
  if (balken.zustand === 'OK') return null;
  const fast = balken.zustand === 'FAST';
  // Schlusslauf 5, H-1 (14.1 Punkte 3 und 5, W-S11): Teil 14 nennt das Kontingent „aufgebraucht“ und sagt, worüber es weitergeht;
  // gesperrt ist nichts. Vor 100 % bleibt „Fast aufgebraucht.“ (KT-13).
  // Stripe-Start Ticket 04: beim Paket ist die Anzahl der Recherchen das Kontingent.
  if (weiter === 'GUTHABEN' && (art === 'KONTINGENT' || art === 'ZUSATZ' || art === 'RECHERCHEN'))
    return fast ? 'Fast aufgebraucht.' : 'Aufgebraucht. Recherchen laufen jetzt über das zusätzliche Guthaben.';
  if (weiter && (art === 'KONTINGENT' || art === 'ZUSATZ')) {
    if (weiter === 'ZUSATZ') return fast ? 'Fast ganz verwendet. Danach geht es mit dem Zusatzkontingent weiter.' : 'Ganz verwendet. Jetzt wird das Zusatzkontingent verwendet.';
    const bis = ueberGroesse !== null ? `, bis ${prozentAnzeige(ueberGroesse)} darüber` : '';
    return fast ? `Fast ganz verwendet. Danach zählt der Verbrauch über dem Kontingent${bis}.` : `Ganz verwendet. Jetzt zählt der Verbrauch über dem Kontingent${bis}.`;
  }
  const weich = balken.modus !== 'HARD';
  if (art === 'KONTINGENT' || art === 'ZUSATZ') return fast ? 'Fast aufgebraucht.'
    : weich ? 'Aufgebraucht. Weitere Recherchen sind erlaubt und werden gezählt.' : 'Aufgebraucht. Neue Recherchen sind bis zum Neubeginn gesperrt.';
  return fast ? 'Grenze fast erreicht.'
    : weich ? 'Grenze erreicht. Weitere Recherchen sind erlaubt und werden gezählt.'
      : art === 'GRENZE' ? 'Grenze erreicht. Neue Recherchen sind für Sie bis zum Neubeginn gesperrt.' : 'Grenze erreicht. Neue Recherchen sind bis zum Neubeginn gesperrt.';
}

/** Was die Sätze über die Töpfe der Firma brauchen (GET /billing/kontingent, organisation; Typen in administration/kontingent.ts). */
type UeberLage = { zustand: BalkenZustand | null };
type FirmenLage = { paket?: boolean; kosten: Pick<Balken, 'zustand'> | null; recherchen?: Pick<Balken, 'zustand'> | null;
  stufe?: BalkenZustand | null; ueber?: UeberLage | null; guthaben?: { traegt: boolean } | null };
const stufeDer = (firma: FirmenLage) => { const balken = firmenBalken(firma); return balken ? firma.stufe ?? balken.zustand : null; };
/** Schlusslauf 6, Nachbesserung 3 (Lage K-5.3): ohne Verbrauch über dem Kontingent oder mit erreichter Obergrenze ist er nicht
 *  (mehr) der nächste Topf; ohne Obergrenze (zustand leer, nur bei „Weitere Recherchen erlauben“) trägt er weiter. */
const ueberVorbei = (ueber: UeberLage | null | undefined) => !ueber || ueber.zustand === 'ERREICHT';
/** Schlusslauf 5, H-1 (3.5, KT-14; 14.1 Punkt 3): nach dem letzten Balken der Firma trägt das zusätzliche Guthaben, solange der
 *  Server organisation.guthaben.traegt meldet. Mit Verbrauch über dem Kontingent erst, wenn dessen Obergrenze erreicht ist
 *  (Schlusslauf 6, Nachbesserung 3, K-5.3); davor ist er der nächste Topf (UEBER). */
export const guthabenWeiter = (firma: Pick<FirmenLage, 'ueber' | 'guthaben'>): Weiter => ueberVorbei(firma.ueber) && firma.guthaben?.traegt === true ? 'GUTHABEN' : null;
/** Schlusslauf 6, Nachbesserung 3 (Lage K-5.3; 3.5, KT-14, 14.1 Punkt 3): was nach dem Kontingent bzw. dem Zusatzkontingent jetzt
 *  trägt. Der freigegebene Verbrauch über dem Kontingent mit Obergrenze, solange diese nicht erreicht ist; danach das zusätzliche
 *  Guthaben, solange es trägt; sonst nichts – dann sagt der Balken dasselbe wie die Zeile KT-49 („… bis zum Neubeginn gesperrt“).
 *  Bisher hieß es nach erreichter Obergrenze weiter „Jetzt zählt der Verbrauch über dem Kontingent“. */
export const weiterNachKontingent = (firma: Pick<FirmenLage, 'ueber' | 'guthaben'>): Weiter =>
  firma.ueber && firma.ueber.zustand !== null && firma.ueber.zustand !== 'ERREICHT' ? 'UEBER' : guthabenWeiter(firma);
/** Für GET /billing/usage (Seite „Kontingent“, CostPanel.tsx): die Antwort nennt kein guthaben. Ohne Verbrauch über dem Kontingent
 *  oder mit erreichter Obergrenze (K-5.3) trägt das Guthaben genau dann, wenn die Stufe erreicht ist, die Grenze hart ist und der
 *  Server trotzdem nicht sperrt (billing/kontingent.ts kontingentImMonat: gesperrt = alle Töpfe erreicht und kein Guthaben trägt). */
export const guthabenAusNutzung = (k: { kosten: Pick<Balken, 'zustand' | 'modus'> | null; stufe?: BalkenZustand | null; ueber?: UeberLage | null; gesperrt?: boolean }) =>
  ({ traegt: ueberVorbei(k.ueber) && !!k.kosten && k.kosten.modus === 'HARD' && (k.stufe ?? k.kosten.zustand) === 'ERREICHT' && k.gesperrt === false });
/** KT-23 „Das Kontingent von {Firma} ist aufgebraucht.“ nur am Ende aller Töpfe: die Stufe am Topf der Sperre (3.5) ist erreicht
 *  und kein zusätzliches Guthaben trägt (H-1). Trägt das Zusatzkontingent, ist die Stufe noch nicht erreicht. */
export const kontingentAufgebraucht = (firma: FirmenLage) => stufeDer(firma) === 'ERREICHT' && firma.guthaben?.traegt !== true;
/** „Wenden Sie sich an die Verwaltung von {Firma}.“ nur, solange der Topf der Sperre knapp oder erreicht ist und kein
 *  zusätzliches Guthaben trägt (H-1): dann muss jemand bei der Firma etwas tun. */
export const verwaltungGefragt = (firma: FirmenLage) => { const stufe = stufeDer(firma); return stufe !== null && stufe !== 'OK' && firma.guthaben?.traegt !== true; };

// Das Kontingent beginnt am ersten Tag des Folgemonats um 00:00 Wiener Zeit neu (F-15 Punkt 7; researchUsage.ts,
// billing/usage.ts, Rechnung in administration/billing/kontingentMonat.ts); gezeigt wird der Tag in österreichischer Zeit.
// Schlusslauf 2, N2 (Frage 9.04, PLAN-1A MA-06): die Uhrzeit steht nur in der Erläuterung, nicht unter jedem Balken.
export function naechsterMonatsbeginn(monat: string): string {
  return naechsterMonatsBeginn(monatsBeginn(monat)).toISOString();
}

const zeitpunkt = new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', day: 'numeric', month: 'long' });
function teile(iso: string) {
  const alle = zeitpunkt.formatToParts(new Date(iso));
  const teil = (typ: Intl.DateTimeFormatPartTypes) => alle.find(t => t.type === typ)?.value ?? '';
  return { tag: `${Number(teil('day'))}. ${teil('month')}` };
}

/** „1. November“ für Sätze wie „Am 1. November beginnt es neu“. */
export const ruecksetzTag = (iso: string) => teile(iso).tag;

/** „Beginnt am 1. November neu“ (2.1, 3.6; früher „Setzt sich am 1. November zurück“). */
export function ruecksetzText(iso: string): string {
  return `Beginnt am ${ruecksetzTag(iso)} neu`;
}

/** aria-valuetext: „37 % verwendet, beginnt am 1. November neu“ (KO-23). zeit ersetzt den Neubeginn, etwa beim
 *  Zusatzkontingent „Gilt bis 31. Oktober“. */
export function ariaText(balken: Pick<Balken, 'art' | 'prozent' | 'unterEinem'>, setztZurueckAm: string, zeit?: string): string {
  const wann = zeit ? zeit.charAt(0).toLowerCase() + zeit.slice(1) : `beginnt am ${ruecksetzTag(setztZurueckAm)} neu`;
  return `${standRechts(balken)}, ${wann}`;
}

const anzahl = (wert: number) => wert.toLocaleString('de-AT');

/** KT-10, KT-16: „Zusätzlich für diesen Monat: 1 % des Kontingents von Talblick Software GmbH“, wenn der Person für diesen
 *  Monat mehr gegeben ist (zusatzProzent, eine Nachkommastelle). Ohne Firmenname endet der Satz nach „des Kontingents“. */
export function zusatzSatz(balken: Pick<Balken, 'art' | 'zusatzProzent'>, firma?: string | null): string | null {
  if (balken.art !== 'KOSTEN' || !balken.zusatzProzent || balken.zusatzProzent <= 0) return null;
  return `Zusätzlich für diesen Monat: ${prozentAnzeige(balken.zusatzProzent)} des Kontingents${firma ? ` von ${firma}` : ''}`;
}

/** KT-11: „47 Recherchen in diesem Monat“ unter dem Balken des Kontingents. */
export const recherchenSatz = (recherchen: number) => recherchen === 1 ? '1 Recherche in diesem Monat' : `${anzahl(recherchen)} Recherchen in diesem Monat`;

/** „12 von 50 Recherchen in diesem Monat“ (Anzahl, Plan 1a Welle 1, W1-G02; „in diesem Monat“ seit Stripe-Start Ticket 03);
 *  für Töpfe der KI kein Satz (KO-23). Ersetzt betraegeSatz, dessen
 *  Zweig mit Beträgen nach KO-23 keinen Leser mehr hat (Suche ohne Grenze in frontend/src und frontend/tests, S3-9).
 *  Stripe-Start Ticket 04: trägt das Guthaben über dem Kontingent (ueberGuthaben), zählen die Recherchen darüber extra
 *  („200 von 200 Recherchen in diesem Monat, +5 über das zusätzliche Guthaben“). */
export function anzahlSatz(balken: Pick<Balken, 'art' | 'verwendet' | 'grenze'>, ueberGuthaben = false): string | null {
  if (balken.art !== 'RECHERCHEN' || balken.verwendet === null || balken.grenze === null) return null;
  const darueber = ueberGuthaben ? Math.max(0, balken.verwendet - balken.grenze) : 0;
  const satz = `${anzahl(ueberGuthaben ? Math.min(balken.verwendet, balken.grenze) : balken.verwendet)} von ${anzahl(balken.grenze)} Recherchen in diesem Monat`;
  return darueber > 0 ? `${satz}, +${anzahl(darueber)} über das zusätzliche Guthaben` : satz;
}

/**
 * Zelle eines Minibalkens in der Spalte „Nutzung“ (KT-40; Plan 1a Welle 1, D-04: nie eine nackte Prozentzahl): mit
 * persönlicher Grenze „84 % der persönlichen Grenze“, an der Grenze nur „Grenze erreicht“; bei der Anzahl „3 von 10
 * Recherchen“, an der Grenze „Grenze erreicht: 10 von 10 Recherchen“.
 */
export function minibalkenText(balken: Pick<Balken, 'art' | 'prozent' | 'zustand' | 'verwendet' | 'grenze' | 'unterEinem'>): string {
  if (balken.art === 'RECHERCHEN') {
    const erreicht = balken.zustand === 'ERREICHT' ? 'Grenze erreicht: ' : '';
    return balken.verwendet !== null && balken.grenze !== null ? `${erreicht}${anzahl(balken.verwendet)} von ${anzahl(balken.grenze)} Recherchen`
      : `${erreicht}${anzahl(balken.prozent)} % der Recherchen im Monat`;
  }
  if (balken.zustand === 'ERREICHT') return 'Grenze erreicht';
  return `${prozentAnzeige(balken.prozent, { unterEinem: balken.unterEinem })} der persönlichen Grenze`;
}

/** KT-40 ohne persönliche Grenze: statt des Strichs, was die Person vom Kontingent verwendet hat. */
export const minibalkenAnteil = (anteil: Anteil) => vomKontingentText(anteil);

/** KT-20: Anfragen an die KI, die noch nicht eingerechnet sind. */
export function nochNichtEingerechnetSatz(anzahlOffen: number | null | undefined): string | null {
  if (!anzahlOffen || anzahlOffen <= 0) return null;
  return anzahlOffen === 1 ? '1 Anfrage an die KI ist noch nicht eingerechnet; der Wert kann noch leicht steigen.'
    : `${anzahl(anzahlOffen)} Anfragen an die KI sind noch nicht eingerechnet; der Wert kann noch leicht steigen.`;
}

/** Spalte „Nutzung“ in Mitglieder: die persönliche Grenze, sonst die Anzahlgrenze, sonst keine. */
export const mitgliedBalken = (m: { kosten: Balken | null; recherchen: Balken | null }, paket = false): Balken | null =>
  personBalken({ person: m, organisation: { paket } }) ?? (paket ? null : m.recherchen);

/** Firmenkontingent aus gemessenen Kosten; mitKontingent prüfen die Kontingenttexte. */
export const firmenBalken = <B>(firma: { paket?: boolean; kosten: B | null; recherchen?: B | null } | null | undefined): B | null =>
  firma?.kosten ?? null;

/** Persönliche Grenze als Anteil des Kostenbudgets, auch bei Paketen. */
export const personBalken = <B>(stand: { person?: { kosten?: B | null; recherchen?: B | null } | null;
  organisation?: { paket?: boolean } | null } | null | undefined): B | null =>
  stand?.person?.kosten ?? null;
